import {useState, useEffect, useCallback, useRef} from 'react'
import {ValueSourceType} from '@/types/attribute-definition'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {endpoints} from '@/api/endpoints'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {useToast} from '@/hooks/use-toast'
import {useConfirmedMutation} from '@/hooks/useConfirmedMutation'
import {useFieldValidation} from '@/hooks/use-field-validation'
import {Button} from '@/components/ui/button'
import {Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Alert, AlertDescription} from '@/components/ui/alert'
import {ToastAction} from '@/components/ui/toast'
import {Info} from 'lucide-react'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {routes} from '@/lib/routes'
import {AttributeValueType} from '@/types/attribute'
import {ALL_OPERATIONS} from '@/types/attribute-operation'
import type {AttributeDefinition, AttributeDefinitionTranslation, MigrationJobAcceptedResponse, UpdateAttributeDefinitionRequest} from '@/types/attribute-definition'
import type {AttributeEntityType} from '@/types/attribute-entity-type'
import {DefinitionFields, type DefinitionFieldValues} from './DefinitionFields'
import {DefinitionImpactConfirmDialog} from './DefinitionImpactConfirmDialog'
import {PatternSuggestDialog} from './PatternSuggestDialog'
import {SqlEditorDialog} from './SqlEditorDialog'
import {entityTypeToTableName} from './entity-type-table-map'

interface DefinitionFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onClosed?: () => void
    entityType: AttributeEntityType
    definition?: AttributeDefinition | null
    onMigrationStarted?: (definitionId: string, jobId: string) => void
}

type CreateResult = AttributeDefinition | MigrationJobAcceptedResponse
type UpdateResult = AttributeDefinition | MigrationJobAcceptedResponse

function isMigrationResult(r: CreateResult | UpdateResult): r is MigrationJobAcceptedResponse {
    return typeof (r as MigrationJobAcceptedResponse).jobId === 'string'
}

export function DefinitionFormDialog({open, onOpenChange, onClosed, entityType, definition, onMigrationStarted}: DefinitionFormDialogProps) {
    const services = useServices()
    const i18n = useI18n()
    const {toast} = useToast()
    const {hasPermission} = useAuth()
    const queryClient = useQueryClient()
    const isEdit = !!definition
    const canUpdate = hasPermission(permissions.attributeDefinitions.update)
    const canMigrate = hasPermission(permissions.attributeDefinitions.migrate)

    const [values, setValues] = useState<DefinitionFieldValues>({
        name: '',
        displayName: '',
        displayNameTranslations: {},
        valueType: AttributeValueType.STRING,
        required: false,
        active: true,
        sortOrder: 0,
        validationRules: null,
        typeCodes: [],
        searchable: true,
        filterable: true,
    })
    const [configuredLocales, setConfiguredLocales] = useState<string[]>([])
    const [nameError, setNameError] = useState<string | null>(null)
    const [sqlEditorOpen, setSqlEditorOpen] = useState(false)
    const [patternSuggestOpen, setPatternSuggestOpen] = useState(false)
    const [migrationAssistContext, setMigrationAssistContext] = useState<string | null>(null)
    const [prefillSql, setPrefillSql] = useState('')
    const definitionRef = useRef(definition)
    if (definition) definitionRef.current = definition
    const valuesRef = useRef(values)
    valuesRef.current = values
    const configuredLocalesRef = useRef(configuredLocales)
    configuredLocalesRef.current = configuredLocales

    const validateUrl = open ? endpoints.attributeDefinitions.validate(entityType) : ''
    const validateBody = {
        name: values.name.trim(),
        displayName: values.displayName.trim(),
        valueType: values.valueType,
        validationRules: values.validationRules,
        required: values.required,
        active: values.active,
        sortOrder: values.sortOrder,
        typeCodes: definition ? (definition.typeCodes ?? []) : values.typeCodes,
    }
    const {fieldErrors, validate: triggerValidation, clearErrors} = useFieldValidation(validateUrl, validateBody)

    const {data: translationData} = useQuery<AttributeDefinitionTranslation>({
        queryKey: ['attribute-definition-translations', entityType, definition?.id],
        queryFn: () => services.attributeDefinitions.translations(entityType, definition!.id) as Promise<AttributeDefinitionTranslation>,
        enabled: isEdit && open && !!definition?.id,
    })

    // Reset form on open/definition change
    useEffect(() => {
        if (definition) {
            setValues({
                name: definition.name,
                displayName: definition.displayName,
                displayNameTranslations: {},
                valueType: definition.valueType,
                required: definition.required,
                active: definition.active,
                sortOrder: definition.sortOrder,
                validationRules: definition.validationRules,
                typeCodes: definition.typeCodes ?? [],
                searchable: definition.searchable,
                filterable: definition.filterable,
            })
            setConfiguredLocales([])
        } else {
            setValues({
                name: '',
                displayName: '',
                displayNameTranslations: {},
                valueType: AttributeValueType.STRING,
                required: false,
                active: true,
                sortOrder: 0,
                validationRules: null,
                typeCodes: [],
                searchable: true,
                filterable: true,
            })
            setConfiguredLocales([])
        }
        setNameError(null)
        clearErrors()
    }, [definition, open]) // eslint-disable-line react-hooks/exhaustive-deps -- reset on open/definition change only

    // Populate per-locale display names once translation data is available
    useEffect(() => {
        if (!isEdit || !open || !translationData) return
        const translations: Record<string, string> = {}
        translationData.configuredLocales.forEach(locale => {
            translations[locale] = translationData.translations[locale]?.label ?? ''
        })
        setValues(prev => ({...prev, displayNameTranslations: translations}))
        setConfiguredLocales(translationData.configuredLocales)
    }, [translationData, isEdit, open])

    const handleChange = useCallback((updates: Partial<DefinitionFieldValues>) => {
        setValues(prev => ({...prev, ...updates}))
        if (updates.name !== undefined) setNameError(null)
        triggerValidation()
    }, [triggerValidation])

    function handleDialogOpenChange(newOpen: boolean) {
        if (!newOpen) {
            onClosed?.()
        }
        onOpenChange(newOpen)
    }

    // eslint-disable-next-line i18next/no-literal-string -- query key
    const queryKey = ['attribute-definitions', entityType] as const

    function buildBody(extra?: Record<string, unknown>): Record<string, unknown> {
        const currentValues = valuesRef.current
        const currentLocales = configuredLocalesRef.current
        const hasTranslations = currentLocales.length > 0
        const effectiveDisplayName = hasTranslations
            ? (currentValues.displayNameTranslations[currentLocales[0]] ?? '').trim()
            : currentValues.displayName.trim()

        const activeSourceType = currentValues.validationRules?.valueSource?.type
        const isListBased = activeSourceType === ValueSourceType.LIST
            || activeSourceType === ValueSourceType.ENUM
            || activeSourceType === ValueSourceType.CUSTOM_LIST
        let validationRules = currentValues.validationRules
        if (isListBased && validationRules) {
            const {pattern, minLength, maxLength, min, max, ...rest} = validationRules
            void pattern; void minLength; void maxLength; void min; void max
            validationRules = rest
        }
        if (validationRules?.maxOccurrences != null && validationRules.maxOccurrences < 1) {
            const {maxOccurrences, ...rest} = validationRules
            void maxOccurrences
            validationRules = rest
        }

        const body: Record<string, unknown> = {
            name: currentValues.name.trim(),
            displayName: effectiveDisplayName,
            valueType: currentValues.valueType,
            required: currentValues.required,
            sortOrder: currentValues.sortOrder,
            active: currentValues.active,
            validationRules,
            typeCodes: currentValues.typeCodes,
            searchable: currentValues.searchable,
            filterable: currentValues.filterable,
        }
        if (isEdit) {
            body.authorityId = definitionRef.current!.authorityId
            body.operations = definitionRef.current!.operations
            body.version = definitionRef.current!.version
        } else {

            body.operations = [...ALL_OPERATIONS]
        }
        if (extra) Object.assign(body, extra)
        return body
    }

    const createMutation = useConfirmedMutation<CreateResult, Record<string, unknown>>({
        mutationFn: (body) => services.attributeDefinitions.create(entityType, body) as Promise<CreateResult>,
        onSuccess: (result) => {
            queryClient.invalidateQueries({queryKey})
            if (isMigrationResult(result)) {
                onMigrationStarted?.(result.jobId, result.jobId)
                toast({
                    description: i18n.attributeDefinitions.migration.createWithMigration,
                    action: <ToastAction altText={i18n.attributeDefinitions.migration.viewJobDashboard} onClick={() => { window.location.href = routes.backgroundJobs.executions.detail(result.jobId) }}>{i18n.attributeDefinitions.migration.viewJobDashboard}</ToastAction>,
                })
            } else {
                toast({description: i18n.attributeDefinitions.createSuccess})
            }
            handleDialogOpenChange(false)
        },
        onError: (error) => {
            if (error.status === 409) {
                toast({description: i18n.attributeDefinitions.nameDuplicate, variant: 'destructive'})
                setNameError(i18n.attributeDefinitions.nameDuplicate)
            }
        },
    })

    const updateMutation = useConfirmedMutation<UpdateResult, Record<string, unknown>>({
        mutationFn: async (body) => {
            const def = definitionRef.current
            if (!def) throw new Error('Cannot update: definition is null')
            const updateBody = body as unknown as UpdateAttributeDefinitionRequest
            const result = await services.attributeDefinitions.update(entityType, def.id, updateBody) as UpdateResult
            const currentLocales = configuredLocalesRef.current
            const currentValues = valuesRef.current
            if (currentLocales.length > 0 && definitionRef.current) {
                const translationsBody = {
                    translations: Object.fromEntries(
                        currentLocales.map(locale => [
                            locale,
                            {label: (currentValues.displayNameTranslations[locale] ?? '').trim(), description: null},
                        ])
                    ),
                }
                await services.attributeDefinitions.updateTranslations(entityType, definitionRef.current.id, translationsBody)
            }
            return result
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({queryKey})
            if (isMigrationResult(result)) {
                onMigrationStarted?.(definitionRef.current!.id, result.jobId)
                toast({
                    description: i18n.attributeDefinitions.migration.jobStarted,
                    action: <ToastAction altText={i18n.attributeDefinitions.migration.viewJobDashboard} onClick={() => { window.location.href = routes.backgroundJobs.executions.detail(result.jobId) }}>{i18n.attributeDefinitions.migration.viewJobDashboard}</ToastAction>,
                })
            } else {
                toast({description: i18n.attributeDefinitions.updateSuccess})
            }
            handleDialogOpenChange(false)
        },
        onError: (error) => {
            if (error.status === 409) {
                toast({
                    description: i18n.attributeDefinitions.optimisticLockConflict,
                    variant: 'destructive',
                })
            }
        },
    })

    function handleSubmit() {
        if (!values.name.trim() || !displayNameValid) return

        if (!isEdit) {
            createMutation.mutation.mutate(buildBody())
        } else {
            updateMutation.mutation.mutate(buildBody())
        }
    }

    function buildMigrationAssistContext(impact: NonNullable<typeof createMutation.impact>): string {
        /* eslint-disable i18next/no-literal-string -- LLM context string */
        const currentValues = valuesRef.current
        const rules = currentValues.validationRules
        const newColumn = AttributeValueType.dbColumn(currentValues.valueType as AttributeValueType)
        const multiValued = (rules?.maxOccurrences ?? 1) > 1
        const effectiveColumn = multiValued ? 'value_json' : newColumn
        const valueDesc = rules?.pattern
            ? `Values follow pattern: ${rules.pattern}`
            : `Values are of type ${currentValues.valueType}`
        const constraints = [
            rules?.minLength != null ? `minLength=${rules.minLength}` : '',
            rules?.maxLength != null ? `maxLength=${rules.maxLength}` : '',
            rules?.min != null ? `min=${rules.min}` : '',
            rules?.max != null ? `max=${rules.max}` : '',
            rules?.maxOccurrences != null ? `maxOccurrences=${rules.maxOccurrences}` : '',
        ].filter(Boolean).join(', ')

        if (isEdit && definition) {
            const oldColumn = AttributeValueType.dbColumn(definition.valueType as AttributeValueType)
            const columnInfo = oldColumn !== newColumn
                ? `Current values in column: ${oldColumn}. New column after migration: ${newColumn}.`
                : `Values stored in column: ${effectiveColumn}.`
            return [
                `Generate ONLY a single UPDATE statement. Do NOT generate INSERT or DELETE.`,
                `Update existing attribute values in table "${entityTypeToTableName(entityType)}" for definition "${definition.name}".`,
                `Problem: ${impact.breakingChanges.join('; ')}.`,
                columnInfo,
                valueDesc + (constraints ? `. Constraints: ${constraints}` : '') + '.',
                `Affected: ${impact.affectedValueCount} values across ${impact.affectedEntityCount} entities.`,
            ].join(' ')
        } else {
            return [
                `Generate ONLY a single INSERT...SELECT statement. Do NOT generate UPDATE or DELETE.`,
                `Insert initial values into table "${entityTypeToTableName(entityType)}" for new required definition "${currentValues.name.trim()}".`,
                `Problem: ${impact.breakingChanges.join('; ')}.`,
                `Values stored in column: ${effectiveColumn}.`,
                valueDesc + (constraints ? `. Constraints: ${constraints}` : '') + '.',
                `Affected: ${impact.affectedEntityCount} entities need an initial value.`,
            ].join(' ')
        }
        /* eslint-enable i18next/no-literal-string */
    }

    function handleSaveWithMigration(activeImpact: NonNullable<typeof createMutation.impact>) {
        const templateSql = activeImpact.migrationSqlTemplate ?? ''
        let ctx = buildMigrationAssistContext(activeImpact)
        if (templateSql) {
            /* eslint-disable i18next/no-literal-string -- LLM context string */
            ctx = `${ctx}\n\nTemplate SQL (refine this):\n${templateSql}`
            /* eslint-enable i18next/no-literal-string */
        }
        setMigrationAssistContext(ctx)
        setPrefillSql(templateSql)
        setSqlEditorOpen(true)
    }

    function handleMigrationSqlApply(sql: string) {
        setSqlEditorOpen(false)
        if (isEdit) {
            updateMutation.confirmAndResend(sql)
        } else {
            createMutation.confirmAndResend(sql)
        }
    }

    const activeImpact = createMutation.impact ?? updateMutation.impact
    const impactDialogOpen = createMutation.impactDialogOpen || updateMutation.impactDialogOpen

    function handleImpactOpenChange(newOpen: boolean) {
        if (!newOpen) {
            createMutation.closeImpactDialog()
            updateMutation.closeImpactDialog()
        }
    }

    function handleImpactCancel() {
        createMutation.cancelImpactDialog()
        updateMutation.cancelImpactDialog()
    }

    function handleImpactConfirm(migrationSql?: string) {
        if (migrationSql !== undefined) {
            handleMigrationSqlApply(migrationSql)
        } else if (activeImpact?.migrationRequired && canMigrate) {
            handleSaveWithMigration(activeImpact)
        } else if (!isEdit) {
            createMutation.confirmAndResend()
        } else {
            updateMutation.confirmAndResend()
        }
    }

    const nameValid = !values.name || (/^[a-zA-Z0-9_]+$/.test(values.name) && values.name.length <= 100)
    const hasFieldErrors = Object.keys(fieldErrors).length > 0
    const displayNameValid = configuredLocales.length > 0
        ? configuredLocales.every(l => (values.displayNameTranslations[l] ?? '').trim().length > 0)
        : values.displayName.trim().length > 0
    const canSubmit = values.name.trim().length > 0 && nameValid && displayNameValid && !hasFieldErrors
    const isPending = createMutation.mutation.isPending || updateMutation.mutation.isPending

    // eslint-disable-next-line i18next/no-literal-string -- test ID prefix
    const testIdPrefix = isEdit ? 'edit' : 'create'

    return (
        <>
            <Dialog open={open} onOpenChange={handleDialogOpenChange}>
                <DialogContent
                    data-testid={`${testIdPrefix}-definition-dialog`}
                    className="sm:max-w-lg"
                >
                    <DialogHeader>
                        <DialogTitle>
                            {isEdit ? i18n.attributeDefinitions.editDefinition : i18n.attributeDefinitions.createDefinition}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            {isEdit ? i18n.attributeDefinitions.editDefinition : i18n.attributeDefinitions.createDefinition}
                        </DialogDescription>
                    </DialogHeader>

                    {!canUpdate && isEdit && (
                        <Alert>
                            <Info className="h-4 w-4" />
                            <AlertDescription>
                                {i18n.attributeDefinitions.noPermissionToEdit}
                            </AlertDescription>
                        </Alert>
                    )}

                    <DialogBody className="py-4">
                        <DefinitionFields
                            values={values}
                            onChange={handleChange}
                            layout="vertical"
                            nameDisabled={isEdit}
                            allDisabled={!canUpdate}
                            nameError={nameError}
                            fieldErrors={fieldErrors}
                            configuredLocales={configuredLocales.length > 0 ? configuredLocales : undefined}
                            onSuggestPattern={() => setPatternSuggestOpen(true)}
                            entityType={entityType}
                            testIdPrefix={`${testIdPrefix}-def`}  
                        />
                    </DialogBody>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => handleDialogOpenChange(false)} data-testid={`${testIdPrefix}-definition-cancel`}>
                            {i18n.common.cancel}
                        </Button>
                        {canUpdate && (
                            <Button onClick={handleSubmit} disabled={!canSubmit || isPending} data-testid={`${testIdPrefix}-definition-submit`}>
                                {isPending && <ButtonSpinner/>}
                                {isEdit ? i18n.common.save : i18n.common.create}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <DefinitionImpactConfirmDialog
                open={impactDialogOpen}
                onOpenChange={handleImpactOpenChange}
                onCancel={handleImpactCancel}
                impact={activeImpact}
                onConfirm={handleImpactConfirm}
                isSaving={isPending}
            />

            <SqlEditorDialog
                open={sqlEditorOpen}
                onOpenChange={(open) => { setSqlEditorOpen(open); if (!open) { setMigrationAssistContext(null); onClosed?.() } }}
                sql={prefillSql}
                onApply={handleMigrationSqlApply}
                applyLabel={i18n.attributeDefinitions.migration.migrate}
                showAssist={canMigrate && !!migrationAssistContext}
                assistContext={migrationAssistContext ?? undefined}
                dryRun
            />

            <PatternSuggestDialog
                open={patternSuggestOpen}
                onOpenChange={setPatternSuggestOpen}
                onPatternAccepted={(pattern) => {
                    handleChange({validationRules: {...values.validationRules, pattern}})
                }}
            />
        </>
    )
}
